/* eslint-disable camelcase */
import { SORT_ORDER, TFilterOptions } from '@evinced-private/ui-common';

import { preprocessSearchValue } from 'src/apps/mobile-common/helpers/MobileSearchHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionOperators,
	Msdk_Test_RunsSortFields,
	Msdk_Test_RunsSortOrder,
	Msdk_Test_RunsTextSearchFields
} from 'src/common/types/graphql.generated';
import { ITableQueryParams } from 'src/common/types/TableQueryTypes';

import { MobileSdkTestsQueryVariables } from '../MobileSdkTests.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';

const testsTextSearchFields: Msdk_Test_RunsTextSearchFields[] = [
	Msdk_Test_RunsTextSearchFields.TestName,
	Msdk_Test_RunsTextSearchFields.RunTime,
	Msdk_Test_RunsTextSearchFields.CriticalIssues,
	Msdk_Test_RunsTextSearchFields.TotalIssues,
	Msdk_Test_RunsTextSearchFields.App,
	Msdk_Test_RunsTextSearchFields.AppVersion,
	Msdk_Test_RunsTextSearchFields.Os,
	Msdk_Test_RunsTextSearchFields.ProductVersion,
	Msdk_Test_RunsTextSearchFields.SdkType,
	Msdk_Test_RunsTextSearchFields.CustomFields
];

const testsSortFieldNamesMap: Record<string, Msdk_Test_RunsSortFields> = {
	testName: Msdk_Test_RunsSortFields.TestName,
	testDate: Msdk_Test_RunsSortFields.RunTime,
	runTime: Msdk_Test_RunsSortFields.RunTime,
	lastModified: Msdk_Test_RunsSortFields.RunTime,
	criticalIssues: Msdk_Test_RunsSortFields.CriticalIssues,
	totalIssues: Msdk_Test_RunsSortFields.TotalIssues,
	totalScans: Msdk_Test_RunsSortFields.TotalScans,
	app: Msdk_Test_RunsSortFields.App,
	appName: Msdk_Test_RunsSortFields.App,
	sdkType: Msdk_Test_RunsSortFields.SdkType,
	os: Msdk_Test_RunsSortFields.Os,
	platform: Msdk_Test_RunsSortFields.Os
};

const testsSortOrderMap: Record<SORT_ORDER, Msdk_Test_RunsSortOrder> = {
	[SORT_ORDER.ASC]: Msdk_Test_RunsSortOrder.Ascending,
	[SORT_ORDER.DESC]: Msdk_Test_RunsSortOrder.Descending
};

export const buildMobileSdkTestsQueryVariables = ({
	pageSize,
	page,
	sort,
	filterOptions
}: ITableQueryParams): MobileSdkTestsQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MobileSdkTestsQueryVariables>({
		sortFieldsMap: testsSortFieldNamesMap,
		sortOrderMap: testsSortOrderMap,
		searchFields: testsTextSearchFields
	});

	// Build core query parameters
	const searchValues = preprocessSearchValue(filterOptions?.searchValue ?? '', true);

	variablesBuilder
		.addPaginationParams(page, pageSize)
		.addSortParam(sort)
		.addTextSearchParam(searchValues);

	// Add dedicated filter fields
	if (filterOptions?.platform?.length) {
		variablesBuilder.addFilterParam('osFilter', filterOptions.platform);
	}

	if (filterOptions?.appName?.length) {
		variablesBuilder.addFilterParam('appFilter', filterOptions.appName);
	}

	if (filterOptions?.appVersion?.length) {
		variablesBuilder.addFilterParam('appVersionFilter', filterOptions.appVersion);
	}

	if (filterOptions?.sdkType?.length) {
		variablesBuilder.addFilterParam('sdkTypeFilter', filterOptions.sdkType);
	}

	if (filterOptions?.sdkVersion?.length) {
		variablesBuilder.addFilterParam('sdkVersionFilter', filterOptions.sdkVersion);
	}

	const knownFields = new Set([
		'sessionsPeriod',
		'searchValue',
		'platform',
		'appName',
		'appVersion',
		'sdkType',
		'sdkVersion',
		'severity'
	]);
	const customFields: TFilterOptions = {};

	if (filterOptions) {
		for (const [key, value] of Object.entries(filterOptions)) {
			if (!knownFields.has(key) && value && Array.isArray(value)) {
				customFields[key] = value;
			}
		}
	}

	// Handle custom fields if any exist
	if (Object.keys(customFields).length > 0) {
		variablesBuilder.addTupleFilterParam(
			'customFieldsFilter',
			customFields,
			ConditionOperators.All
		);
	}

	// Handle period filter
	const selectedPeriod = filterOptions?.sessionsPeriod?.[0];
	if (selectedPeriod?.value !== 'all') {
		variablesBuilder.addConditionParam(
			DAYS_AMOUNT_FILTER_PARAM_KEY,
			filterOptions.sessionsPeriod?.map((p) => Number(p.value))
		);
	}

	return variablesBuilder.getVariables();
};
